import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminActivity = () => {
  const { token } = useAuth();
  const [activityLogs, setActivityLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [limit, setLimit] = useState(50);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchActivity = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activity-logs?limit=${limit}`, { headers });
      setActivityLogs(response.data);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [token, limit]);

  const getActionColor = (action) => {
    if (action.includes("created") || action.includes("added")) return "bg-green-500";
    if (action.includes("updated") || action.includes("changed")) return "bg-blue-500";
    if (action.includes("deleted") || action.includes("removed")) return "bg-red-500";
    return "bg-primary";
  };

  const getEntityBadge = (entityType) => {
    const badges = {
      lead: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      client: "bg-green-500/20 text-green-400 border-green-500/30",
      appointment: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      pricing: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      availability: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      engagement: "bg-pink-500/20 text-pink-400 border-pink-500/30",
    };
    return badges[entityType] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-activity">
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-white">
            <Activity className="w-5 h-5 text-primary" />
            Activity Log ({activityLogs.length})
          </CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="bg-navy-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value={25}>Last 25</option>
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
              <option value={500}>Last 500</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchActivity}
              className="border-slate-700 text-slate-300 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activityLogs.length > 0 ? (
            <div className="space-y-3">
              {activityLogs.map((log, index) => (
                <div
                  key={log.id || index}
                  className="flex items-start gap-4 p-4 rounded-lg bg-navy-950/50 border border-slate-800 hover:border-slate-700 transition-colors"
                  data-testid={`activity-${index}`}
                >
                  <div className={`w-2 h-2 rounded-full ${getActionColor(log.action)} mt-2 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-white font-medium capitalize">
                        {log.action.replace(/_/g, " ")}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getEntityBadge(log.entity_type)}`}>
                        {log.entity_type}
                      </span>
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {log.details.changes && Array.isArray(log.details.changes) 
                          ? `Changed: ${log.details.changes.join(", ")}` 
                          : JSON.stringify(log.details)}
                      </p>
                    )}
                    <p className="text-xs text-slate-600 mt-1">
                      {new Date(log.timestamp).toLocaleString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No activity recorded yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminActivity;
